import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Plus,
  MagnifyingGlass,
  Export,
  FileCsv,
  FileXls,
  FilePdf,
  Receipt,
  Eye,
  PaperPlaneTilt,
  CheckCircle,
  Printer,
  DownloadSimple,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
];

const getCatLabel = (cat, t) => {
  if (!cat || cat === 'uncategorized') return t('uncategorized');
  const key = `cat_${cat}`;
  const label = t(key);
  return label !== key ? label : cat;
};

export default function Statements() {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [statements, setStatements] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Generate dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    building_id: '',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    notes: ''
  });
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statementsRes, buildingsRes] = await Promise.all([
        axios.get(`${API}/statements`),
        axios.get(`${API}/buildings`)
      ]);
      setStatements(statementsRes.data);
      setBuildings(buildingsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/statements/generate`, formData);
      toast.success(t('savedSuccess'));
      setGenerateDialogOpen(false);
      setFormData({
        building_id: '',
        period_month: new Date().getMonth() + 1,
        period_year: new Date().getFullYear(),
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleMarkSent = async (statementId) => {
    try {
      await axios.put(`${API}/statements/${statementId}/status?status=sent`);
      toast.success(t('savedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleMarkPaid = async (statementId) => {
    try {
      await axios.put(`${API}/statements/${statementId}/status?status=paid`);
      toast.success(t('savedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const openViewDialog = (statement) => {
    setSelectedStatement(statement);
    setViewDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'status-paid';
      case 'sent': return 'status-upcoming';
      case 'draft': return 'status-pending';
      default: return 'status-pending';
    }
  };

  const filteredStatements = statements.filter(s => {
    const matchesSearch = s.building_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Export statement to PDF
  const exportStatementToPDF = (stmt) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('CleanPro Ops', 14, 20);
    doc.setFontSize(12);
    doc.text(t('statements'), 14, 30);
    
    // Building info
    doc.setFontSize(14);
    doc.text(stmt.building_name, 14, 45);
    doc.setFontSize(10);
    doc.text(`${t('statementPeriod')}: ${t(MONTHS[stmt.period_month - 1])} ${stmt.period_year}`, 14, 52);
    
    // Fee breakdown table
    const tableData = [];
    
    if (stmt.building_type === 'managed' && stmt.management_fee > 0) {
      tableData.push([t('managementFee'), `€${stmt.management_fee.toFixed(2)}`]);
      tableData.push([`${t('vat')} (${stmt.vat_rate}%)`, `€${stmt.management_vat.toFixed(2)}`]);
    }
    
    tableData.push([t('cleaningFee'), `€${stmt.cleaning_fee.toFixed(2)}`]);
    tableData.push([`${t('vat')} (${stmt.vat_rate}%)`, `€${stmt.cleaning_vat.toFixed(2)}`]);
    
    tableData.push(['', '']);
    tableData.push([t('subtotal'), `€${stmt.total_before_vat.toFixed(2)}`]);
    tableData.push([t('vat'), `€${stmt.total_vat.toFixed(2)}`]);
    tableData.push([t('totalAmount'), `€${stmt.total_amount.toFixed(2)}`]);
    
    if (stmt.expenses_total > 0) {
      tableData.push(['', '']);
      tableData.push([t('expenses'), `€${stmt.expenses_total.toFixed(2)}`]);
      // Per-category breakdown
      if (stmt.expenses_by_category) {
        Object.entries(stmt.expenses_by_category).sort(([a], [b]) => a.localeCompare(b)).forEach(([cat, amount]) => {
          tableData.push([`  ${getCatLabel(cat, t)}`, `€${amount.toFixed(2)}`]);
        });
      }
      tableData.push([`  ${t('paidExpenses')}`, `€${(stmt.expenses_paid || 0).toFixed(2)}`]);
      tableData.push([`  ${t('unpaidExpenses')}`, `€${(stmt.expenses_unpaid || 0).toFixed(2)}`]);
    }
    
    if (stmt.elevator_expenses_total > 0) {
      tableData.push(['', '']);
      tableData.push([t('elevatorExpenses'), `€${stmt.elevator_expenses_total.toFixed(2)}`]);
    }
    
    autoTable(doc, {
      startY: 60,
      head: [[t('feeBreakdown'), t('amount')]],
      body: tableData,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [24, 24, 27] },
      footStyles: { fillColor: [24, 24, 27] }
    });
    
    doc.save(`statement_${stmt.building_name}_${stmt.period_month}_${stmt.period_year}.pdf`);
  };

  const printStatementPDF = (stmt) => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text('CleanPro Ops', 14, 20);
    doc.setFontSize(12);
    doc.text(t('statements'), 14, 30);
    doc.setFontSize(14);
    doc.text(stmt.building_name, 14, 45);
    doc.setFontSize(10);
    doc.text(`${t('statementPeriod')}: ${t(MONTHS[stmt.period_month - 1])} ${stmt.period_year}`, 14, 52);
    const tableData = [];
    if (stmt.building_type === 'managed' && stmt.management_fee > 0) {
      tableData.push([t('managementFee'), `€${stmt.management_fee.toFixed(2)}`]);
      tableData.push([`${t('vat')} (${stmt.vat_rate}%)`, `€${stmt.management_vat.toFixed(2)}`]);
    }
    tableData.push([t('cleaningFee'), `€${stmt.cleaning_fee.toFixed(2)}`]);
    tableData.push([`${t('vat')} (${stmt.vat_rate}%)`, `€${stmt.cleaning_vat.toFixed(2)}`]);
    tableData.push(['', '']);
    tableData.push([t('subtotal'), `€${stmt.total_before_vat.toFixed(2)}`]);
    tableData.push([t('vat'), `€${stmt.total_vat.toFixed(2)}`]);
    tableData.push([t('totalAmount'), `€${stmt.total_amount.toFixed(2)}`]);
    if (stmt.expenses_total > 0) {
      tableData.push(['', '']);
      tableData.push([t('expenses'), `€${stmt.expenses_total.toFixed(2)}`]);
      if (stmt.expenses_by_category) {
        Object.entries(stmt.expenses_by_category).sort(([a], [b]) => a.localeCompare(b)).forEach(([cat, amount]) => {
          tableData.push([`  ${getCatLabel(cat, t)}`, `€${amount.toFixed(2)}`]);
        });
      }
      tableData.push([`  ${t('paidExpenses')}`, `€${(stmt.expenses_paid || 0).toFixed(2)}`]);
      tableData.push([`  ${t('unpaidExpenses')}`, `€${(stmt.expenses_unpaid || 0).toFixed(2)}`]);
    }
    if (stmt.elevator_expenses_total > 0) {
      tableData.push(['', '']);
      tableData.push([t('elevatorExpenses'), `€${stmt.elevator_expenses_total.toFixed(2)}`]);
    }
    autoTable(doc, {
      startY: 60,
      head: [[t('feeBreakdown'), t('amount')]],
      body: tableData,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [24, 24, 27] },
      footStyles: { fillColor: [24, 24, 27] }
    });
    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(blobUrl, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        setTimeout(() => { printWindow.print(); }, 500);
      });
    } else {
      // Popup blocked — fallback to download
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'statement.pdf';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 1000);
    }
  };

  // Export all to Excel
  const exportToExcel = () => {
    const data = filteredStatements.map(s => ({
      [t('buildings')]: s.building_name,
      [t('statementPeriod')]: `${t(MONTHS[s.period_month - 1])} ${s.period_year}`,
      [t('managementFee')]: s.management_fee,
      [t('cleaningFee')]: s.cleaning_fee,
      [t('vat')]: s.total_vat,
      [t('totalAmount')]: s.total_amount,
      [t('status')]: t(s.status)
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Statements');
    XLSX.writeFile(wb, 'statements.xlsx');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="statements-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              placeholder={t('search') + '...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-zinc-200 rounded-none"
              data-testid="search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] rounded-none" data-testid="status-filter">
              <SelectValue placeholder={t('status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              <SelectItem value="draft">{t('draft')}</SelectItem>
              <SelectItem value="sent">{t('sent')}</SelectItem>
              <SelectItem value="paid">{t('paid')}</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-zinc-200 rounded-none" data-testid="export-btn">
                <Export size={18} className="mr-2" />
                {t('export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToExcel}><FileXls size={18} className="mr-2" />Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isAdmin && (
            <Button onClick={() => setGenerateDialogOpen(true)} className="bg-zinc-950 rounded-none" data-testid="generate-statement-btn">
              <Plus size={18} className="mr-2" />
              {t('generateStatement')}
            </Button>
          )}
        </div>

        {/* Table - Desktop */}
        <div className="hidden md:block bg-white border border-zinc-200 overflow-x-auto">
          {filteredStatements.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">{t('noStatements')}</div>
          ) : (
            <table className="excel-table" data-testid="statements-table">
              <thead>
                <tr>
                  <th>{t('buildings')}</th>
                  <th>{t('statementPeriod')}</th>
                  <th>{t('managementFee')}</th>
                  <th>{t('cleaningFee')}</th>
                  <th>{t('vat')}</th>
                  <th>{t('totalAmount')}</th>
                  <th>{t('expenses')}</th>
                  <th>{t('elevator')}</th>
                  <th>{t('status')}</th>
                  <th className="w-40">{t('edit')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredStatements.map((stmt) => (
                  <tr key={stmt.statement_id} data-testid={`statement-row-${stmt.statement_id}`}>
                    <td className="font-medium">{stmt.building_name}</td>
                    <td className="font-mono text-sm">
                      {t(MONTHS[stmt.period_month - 1])} {stmt.period_year}
                    </td>
                    <td className="font-mono">
                      {stmt.building_type === 'managed' ? `€${stmt.management_fee.toFixed(2)}` : '-'}
                    </td>
                    <td className="font-mono">€{stmt.cleaning_fee.toFixed(2)}</td>
                    <td className="font-mono text-zinc-500">€{stmt.total_vat.toFixed(2)}</td>
                    <td className="font-mono font-semibold">€{stmt.total_amount.toFixed(2)}</td>
                    <td>
                      {stmt.expenses_total > 0 ? (
                        <span className="font-mono text-sm">€{stmt.expenses_total.toFixed(2)}</span>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </td>
                    <td>
                      {stmt.elevator_expenses_total > 0 ? (
                        <span className="font-mono text-sm">€{stmt.elevator_expenses_total.toFixed(2)}</span>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </td>
                    <td>
                      <span className={getStatusColor(stmt.status)}>{t(stmt.status)}</span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openViewDialog(stmt)}
                          className="p-2 hover:bg-zinc-100 transition-colors"
                          title={t('viewStatement')}
                          data-testid={`view-statement-${stmt.statement_id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => printStatementPDF(stmt)}
                          className="p-2 hover:bg-blue-50 text-zinc-500 hover:text-blue-600 transition-colors"
                          title={t('printPdf')}
                          data-testid={`print-pdf-${stmt.statement_id}`}
                        >
                          <Printer size={18} />
                        </button>
                        <button
                          onClick={() => exportStatementToPDF(stmt)}
                          className="p-2 hover:bg-zinc-100 transition-colors"
                          title={t('downloadPdf')}
                          data-testid={`export-pdf-${stmt.statement_id}`}
                        >
                          <DownloadSimple size={18} />
                        </button>
                        {stmt.status === 'draft' && (
                          <button
                            onClick={() => handleMarkSent(stmt.statement_id)}
                            className="p-2 hover:bg-blue-50 text-blue-600 transition-colors"
                            title={t('markAsSent')}
                            data-testid={`mark-sent-${stmt.statement_id}`}
                          >
                            <PaperPlaneTilt size={18} />
                          </button>
                        )}
                        {stmt.status !== 'paid' && (
                          <button
                            onClick={() => handleMarkPaid(stmt.statement_id)}
                            className="p-2 hover:bg-emerald-50 text-emerald-600 transition-colors"
                            title={t('markAsPaid')}
                            data-testid={`mark-paid-${stmt.statement_id}`}
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Cards - Mobile */}
        <div className="md:hidden space-y-3">
          {filteredStatements.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
              {t('noStatements')}
            </div>
          ) : (
            filteredStatements.map((stmt) => (
              <div key={stmt.statement_id} className="mobile-card" data-testid={`statement-card-${stmt.statement_id}`}>
                <div className="mobile-card-header">
                  <div>
                    <h3 className="mobile-card-title">{stmt.building_name}</h3>
                    <p className="text-sm text-zinc-500 font-mono">
                      {t(MONTHS[stmt.period_month - 1])} {stmt.period_year}
                    </p>
                  </div>
                  <span className={getStatusColor(stmt.status)}>{t(stmt.status)}</span>
                </div>
                
                <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200">
                  {stmt.building_type === 'managed' && stmt.management_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">{t('managementFee')}</span>
                      <span className="font-mono">€{stmt.management_fee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">{t('cleaningFee')}</span>
                    <span className="font-mono">€{stmt.cleaning_fee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">{t('vat')}</span>
                    <span className="font-mono">€{stmt.total_vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t border-zinc-200 pt-2 mt-2">
                    <span>{t('totalAmount')}</span>
                    <span className="font-mono">€{stmt.total_amount.toFixed(2)}</span>
                  </div>
                  {stmt.expenses_total > 0 && (
                    <div className="flex justify-between text-sm text-amber-700 border-t border-zinc-200 pt-2 mt-2">
                      <span>{t('expenses')}</span>
                      <span className="font-mono">€{stmt.expenses_total.toFixed(2)}</span>
                    </div>
                  )}
                  {stmt.elevator_expenses_total > 0 && (
                    <div className="flex justify-between text-sm text-blue-700">
                      <span>{t('elevator')}</span>
                      <span className="font-mono">€{stmt.elevator_expenses_total.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printStatementPDF(stmt)}
                    className="flex-1 rounded-none"
                    data-testid={`print-stmt-mobile-${stmt.statement_id}`}
                  >
                    <Printer size={16} className="mr-1" />
                    {t('printPdf')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportStatementToPDF(stmt)}
                    className="flex-1 rounded-none"
                    data-testid={`download-stmt-mobile-${stmt.statement_id}`}
                  >
                    <DownloadSimple size={16} className="mr-1" />
                    PDF
                  </Button>
                  {stmt.status !== 'paid' && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkPaid(stmt.statement_id)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-none"
                    >
                      <CheckCircle size={16} className="mr-1" />
                      {t('markAsPaid')}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Generate Statement Dialog */}
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('generateStatement')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div>
                <Label>{t('buildings')}</Label>
                <Select
                  value={formData.building_id}
                  onValueChange={(val) => setFormData({ ...formData, building_id: val })}
                  required
                >
                  <SelectTrigger className="mt-1 rounded-none" data-testid="building-select">
                    <SelectValue placeholder={t('buildings')} />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings.map(b => (
                      <SelectItem key={b.building_id} value={b.building_id}>
                        {b.name} ({t(b.building_type)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('month')}</Label>
                  <Select
                    value={formData.period_month.toString()}
                    onValueChange={(val) => setFormData({ ...formData, period_month: parseInt(val) })}
                  >
                    <SelectTrigger className="mt-1 rounded-none" data-testid="month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((month, idx) => (
                        <SelectItem key={month} value={(idx + 1).toString()}>{t(month)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('year')}</Label>
                  <Input
                    type="number"
                    value={formData.period_year}
                    onChange={(e) => setFormData({ ...formData, period_year: parseInt(e.target.value) })}
                    className="mt-1 rounded-none font-mono"
                    min="2020"
                    max="2030"
                    data-testid="year-input"
                  />
                </div>
              </div>

              {formData.building_id && (
                <div className="p-3 bg-zinc-50 border border-zinc-200">
                  <p className="text-sm font-medium mb-2">{t('feeBreakdown')}</p>
                  {(() => {
                    const building = buildings.find(b => b.building_id === formData.building_id);
                    if (!building) return null;
                    const isManaged = building.building_type === 'managed';
                    const managementFee = isManaged ? (building.management_fee || 0) : 0;
                    const cleaningFee = building.cleaning_fee || 0;
                    const vatRate = building.vat_rate || 24;
                    const subtotal = managementFee + cleaningFee;
                    const vat = subtotal * (vatRate / 100);
                    const total = subtotal + vat;
                    
                    return (
                      <div className="space-y-1 text-sm">
                        {isManaged && (
                          <div className="flex justify-between">
                            <span className="text-zinc-500">{t('managementFee')}</span>
                            <span className="font-mono">€{managementFee.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-zinc-500">{t('cleaningFee')}</span>
                          <span className="font-mono">€{cleaningFee.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500">{t('vat')} ({vatRate}%)</span>
                          <span className="font-mono">€{vat.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t border-zinc-200 pt-1">
                          <span>{t('totalAmount')}</span>
                          <span className="font-mono">€{total.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <div>
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  data-testid="notes-input"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setGenerateDialogOpen(false)} className="rounded-none">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="generate-btn">
                  <Receipt size={18} className="mr-2" />
                  {t('generateStatement')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Statement Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('statementBreakdown')}
              </DialogTitle>
            </DialogHeader>
            {selectedStatement && (
              <div className="space-y-4">
                <div className="border-b border-zinc-200 pb-4">
                  <h3 className="font-semibold text-lg" data-testid="stmt-view-building-name">{selectedStatement.building_name}</h3>
                  {selectedStatement.building_address && (
                    <p className="text-sm text-zinc-500" data-testid="stmt-view-building-address">
                      {selectedStatement.building_address}
                    </p>
                  )}
                  <p className="text-zinc-500">
                    {t(MONTHS[selectedStatement.period_month - 1])} {selectedStatement.period_year}
                  </p>
                </div>
                
                <div className="space-y-3">
                  {selectedStatement.building_type === 'managed' && selectedStatement.management_fee > 0 && (
                    <>
                      <div className="flex justify-between items-center">
                        <span>{t('managementFee')}</span>
                        <span className="font-mono font-semibold">€{selectedStatement.management_fee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-zinc-500 pl-4">
                        <span>{t('vat')} ({selectedStatement.vat_rate}%)</span>
                        <span className="font-mono">€{selectedStatement.management_vat.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span>{t('cleaningFee')}</span>
                    <span className="font-mono font-semibold">€{selectedStatement.cleaning_fee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-zinc-500 pl-4">
                    <span>{t('vat')} ({selectedStatement.vat_rate}%)</span>
                    <span className="font-mono">€{selectedStatement.cleaning_vat.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="border-t border-zinc-200 pt-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span>{t('subtotal')}</span>
                    <span className="font-mono">€{selectedStatement.total_before_vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>{t('vat')}</span>
                    <span className="font-mono">€{selectedStatement.total_vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-semibold border-t border-zinc-200 pt-2">
                    <span>{t('totalAmount')}</span>
                    <span className="font-mono">€{selectedStatement.total_amount.toFixed(2)}</span>
                  </div>
                  {selectedStatement.expenses_total > 0 && (
                    <div className="border-t border-zinc-200 pt-3 mt-3 space-y-1">
                      <div className="flex justify-between items-center text-amber-700">
                        <span className="font-medium">{t('expenses')}</span>
                        <span className="font-mono font-semibold">€{selectedStatement.expenses_total.toFixed(2)}</span>
                      </div>
                      {/* Per-category breakdown */}
                      {selectedStatement.expenses_by_category && Object.keys(selectedStatement.expenses_by_category).length > 0 && (
                        <div className="pl-4 space-y-1 pt-1">
                          {Object.entries(selectedStatement.expenses_by_category).sort(([a], [b]) => a.localeCompare(b)).map(([cat, amount]) => (
                            <div key={cat} className="flex justify-between items-center text-sm text-zinc-500">
                              <span>{getCatLabel(cat, t)}</span>
                              <span className="font-mono">€{amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-between items-center text-sm text-zinc-500">
                        <span>{t('paidExpenses')}</span>
                        <span className="font-mono">€{(selectedStatement.expenses_paid || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-zinc-500">
                        <span>{t('unpaidExpenses')}</span>
                        <span className="font-mono">€{(selectedStatement.expenses_unpaid || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  {selectedStatement.elevator_expenses_total > 0 && (
                    <div className="border-t border-zinc-200 pt-3 mt-3 space-y-1">
                      <div className="flex justify-between items-center text-blue-700">
                        <span className="font-medium">{t('elevatorExpenses')}</span>
                        <span className="font-mono font-semibold">€{selectedStatement.elevator_expenses_total.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button 
                variant="outline"
                onClick={() => selectedStatement && printStatementPDF(selectedStatement)}
                className="rounded-none"
                data-testid="print-stmt-detail"
              >
                <Printer size={18} className="mr-2" />
                {t('printPdf')}
              </Button>
              <Button 
                onClick={() => selectedStatement && exportStatementToPDF(selectedStatement)}
                className="bg-zinc-950 rounded-none"
                data-testid="download-stmt-detail"
              >
                <DownloadSimple size={18} className="mr-2" />
                {t('downloadPdf')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
